import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { I18n } from "@aws-amplify/core";
import { includes, isEmpty, map, get, size } from "lodash";

import { fetchPost } from "../../../api/fetch";

import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";

import CustomInput from "../../Common/CustomInput";
import CustomSelect from "../../Common/CustomSelect";
import CustomButton from "../../Common/CustomButton";
import FocusPhoto from "./FocusPhoto";

import {
  getLatestValue,
  getOcrLprConfirmationCorrection,
} from "../../../utils/event";
import { getLov } from "../../../utils/lov";
import { getUser } from "../../../utils/users";
import { formatDate } from "../../../utils/date";
import { getAttributeValue } from "../../../utils/profile";
import { getSubEvent } from "../../../utils/detectionDetails";

import { ATTRIBUTE, CORRECTION_TYPE } from "../../../constants/attributes";
import { LPR, OCR } from "../../../constants/common";

import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";

const steps = {
  CONTEXT: "context",
  LOADING: "loading",
  INITIAL: "initial",
  CONFIRMED_VALUE: "confirmed_value",
  INCORRECT_VALUE: "incorrect_value",
  CORRECTED_VALUE: "corrected_value",
};

const Correction = (props) => {
  const {
    language,
    focusElement,
    detectionEventGroup,
    reloadEventGroupAndFocusElement,
  } = props;
  const [step, setStep] = React.useState(steps.LOADING);
  const [elementStatus, setElementStatus] = React.useState({});
  const [inputValue, setInputValue] = React.useState("");
  const [forceEdition, setForceEdition] = React.useState(false);
  const [valueType, setValueType] = React.useState("null");
  const [correctionTypeOptions, setCorrectionTypeOptions] = React.useState();

  React.useEffect(() => {
    const ac = new AbortController();

    const fetchCorrectionTypeOptions = async () => {
      let correctionTypes = await getLov(ATTRIBUTE.CORRECTION_TYPE);
      if (!ac.signal.aborted) {
        let correctionTypeOptions = [
          { id: "null", name: "Null" },
          ...map(correctionTypes, (v) => ({
            id: v.id,
            name: v[`name_${language}`],
          })),
        ];
        setCorrectionTypeOptions(correctionTypeOptions);
      }
    };
    fetchCorrectionTypeOptions();

    return () => {
      ac.abort();
    };
     
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const checkElementStatus = async () => {
      let element = getOcrLprConfirmationCorrection(
        focusElement,
        detectionEventGroup
      );
      try {
        for (let i = 0; i < element.events.length; i++) {
          if (element.events[i] && !isNaN(element.events[i].created_by)) {
            let createdBy = await getUser(element.events[i].created_by);
            element.events[i].created_by =
              createdBy.profile.firstname + " " + createdBy.profile.lastname;
          }
        }
      } catch (e) {
        console.log(e);
      }
      setElementStatus(element);
      setStep(steps.INITIAL);
    };
    if (focusElement.type.includes("CONTEXT")) {
      setElementStatus({});
      setStep(steps.CONTEXT);
    } else {
      setStep(steps.LOADING);
      setElementStatus({});
      checkElementStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectionEventGroup, focusElement, focusElement.type]);

  // Debug useEffect
  // React.useEffect(() => {
  //   console.log("Correction - step transition to:", step);
  //   switch (step) {
  //     case steps.INCORRECT_VALUE:
  //       console.log("Correction - valueType:", valueType);
  //       break;
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [step]);

  // Debug useEffect
  // React.useEffect(() => {
  //   console.log("Correction - focusElement:", focusElement)
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [focusElement]);

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  const confirmValue = async () => {
    if (detectionEventGroup.id) {
      let matchRequestEvent;
      let body = { event_group_id: detectionEventGroup.id };
      if (focusElement.type === "OCR") {
        matchRequestEvent = getSubEvent([71], detectionEventGroup);
        matchRequestEvent.forEach((e) => {
          let temp = getSubEvent([62], e);
          temp.forEach((se) => {
            let croppedImg = getAttributeValue(se, 3);
            if (
              croppedImg === focusElement.url ||
              includes(croppedImg, focusElement.url) ||
              includes(focusElement.url, croppedImg)
            ) {
              matchRequestEvent = [se];
            }
          });
        });
        body.type_id = 76;
      } else if (focusElement.type === "LPR") {
        matchRequestEvent = getSubEvent([72], detectionEventGroup);
        matchRequestEvent.forEach((e) => {
          let temp = getSubEvent([56], e);
          temp.forEach((se) => {
            let croppedImg = getAttributeValue(se, 3);
            if (
              croppedImg === focusElement.url ||
              includes(croppedImg, focusElement.url) ||
              includes(focusElement.url, croppedImg)
            ) {
              matchRequestEvent = [se];
            }
          });
        });
        body.type_id = 78;
      }
      if (
        matchRequestEvent &&
        matchRequestEvent.length > 0 &&
        matchRequestEvent[0].id
      ) {
        body.parent_event_id = matchRequestEvent[0].id;
        try {
          await fetchPost("events", body);
          reloadEventGroupAndFocusElement();
        } catch (e) {
          console.log(e);
        }
      }
    }
  };

  const correctValue = async () => {
    if (detectionEventGroup.id) {
      let matchRequestEvent;
      let body = { event_group_id: detectionEventGroup.id, attributes: [] };
      if (focusElement.type === OCR) {
        matchRequestEvent = getSubEvent([71], detectionEventGroup);
        matchRequestEvent.forEach((e) => {
          let temp = getSubEvent([62], e);
          temp.forEach((se) => {
            let croppedImg = getAttributeValue(se, 3);
            if (
              croppedImg === focusElement.url ||
              includes(croppedImg, focusElement.url) ||
              includes(focusElement.url, croppedImg)
            ) {
              matchRequestEvent = [se];
            }
          });
        });
        body.type_id = 75;
        if (
          elementStatus.values &&
          elementStatus.values.length > 0 &&
          elementStatus.values[0] !== inputValue
        ) {
          body.attributes = [{ id: 50, value: inputValue }];
        }
      } else if (focusElement.type === "LPR") {
        matchRequestEvent = getSubEvent([72], detectionEventGroup);
        matchRequestEvent.forEach((e) => {
          let temp = getSubEvent([56], e);
          temp.forEach((se) => {
            let croppedImg = getAttributeValue(se, 3);
            if (
              croppedImg === focusElement.url ||
              includes(croppedImg, focusElement.url) ||
              includes(focusElement.url, croppedImg)
            ) {
              matchRequestEvent = [se];
            }
          });
        });
        body.type_id = 77;
        if (
          elementStatus.values &&
          elementStatus.values.length > 0 &&
          elementStatus.values[0] !== inputValue
        ) {
          body.attributes = [{ id: 44, value: inputValue }];
        }
      }
      if (
        matchRequestEvent &&
        matchRequestEvent.length > 0 &&
        matchRequestEvent[0].id
      ) {
        // Value type has been corrected
        let valueTypeString =
          valueType === 84 ? OCR : valueType === 85 ? LPR : "null";
        if (
          valueTypeString &&
          valueTypeString !== "null" &&
          focusElement.type !== valueTypeString &&
          [OCR, LPR].includes(valueTypeString)
        ) {
          body.attributes.push({
            id: 140,
            value: valueType,
          });
        }
        body.parent_event_id = matchRequestEvent[0].id;
        try {
          await fetchPost("events", body);
          reloadEventGroupAndFocusElement();
          setForceEdition(false);
        } catch (e) {
          console.log(e);
        }
      }
    }
  };

  const handleIncorrectValueTransition = () => {
    let focusImageType = focusElement?.type;
    switch (focusImageType) {
      case OCR:
        setValueType(CORRECTION_TYPE.OCR);
        break;
      case LPR:
        setValueType(CORRECTION_TYPE.LPR);
        break;
      default:
        setValueType("null");
    }
    setInputValue(
      elementStatus.values && elementStatus.values.length > 0
        ? elementStatus.values[0]
        : ""
    );
    setStep(steps.INCORRECT_VALUE);
  };

  const renderValues = () => {
    let [latestValue, mostRecentEvent] = getLatestValue(focusElement);

    // Check for type correction
    let typeCorrection = false;
    if (mostRecentEvent.attributes) {
      typeCorrection = mostRecentEvent.attributes.filter(
        (attr) => attr.id === 140
      );
      if (typeCorrection.length > 0) {
        typeCorrection =
          typeCorrection[0].value === 84
            ? OCR
            : typeCorrection[0].value === 85
            ? LPR
            : false;
      } else {
        typeCorrection = false;
      }
    }

    let fontSize = "50px";
    if (size(latestValue) > 10) {
      fontSize = 50 - (size(latestValue) - 10) * 3;
      fontSize = fontSize.toString() + "px";
    }

    return (
      <Grid item>
        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{ maxHeight: "135px", overflow: "auto" }}
        >
          <Grid item>
            <Grid container wrap="nowrap">
              <Grid
                item
                xs
                className="bold-500 flexCenter"
                style={{
                  justifyContent: "center",
                  maxHeight: "60px",
                  maxWidth: "334px",
                  fontSize: fontSize,
                  lineHeight: "60px",
                  color: !isEmpty(mostRecentEvent)
                    ? colors.green
                    : colors.black87,
                }}
              >
                {latestValue}
              </Grid>
              {!forceEdition &&
                elementStatus.events &&
                elementStatus.events.length > 0 && (
                  <Grid item>
                    <img
                      style={{ cursor: "pointer", padding: "5px" }}
                      src={images.editIconColored}
                      alt="edit"
                      onClick={() => {
                        setForceEdition(true);
                        setStep(steps.INCORRECT_VALUE);
                        setInputValue(
                          elementStatus.values &&
                            elementStatus.values.length > 0
                            ? elementStatus.values[0]
                            : ""
                        );
                      }}
                    />
                  </Grid>
                )}
            </Grid>
          </Grid>
          {elementStatus.events &&
            elementStatus.events
              .sort((a, b) => new Date(b.created_on) - new Date(a.created_on))
              .map((e, index) => (
                <Grid item key={index} style={{ marginBottom: "10px" }}>
                  <Grid container direction="column">
                    {[75, 77].includes(e.type.id) &&
                    getAttributeValue(e, e.type.id === 75 ? 50 : 44) ? (
                      <>
                        <Grid
                          item
                          className="text-54"
                          style={{
                            textDecoration: "line-through",
                            fontSize: "18px",
                          }}
                        >
                          {elementStatus.events.length > index + 1 &&
                          elementStatus.events[index + 1].attributes
                            ? getAttributeValue(
                                elementStatus.events[index + 1],
                                focusElement.type === "OCR" ? 50 : 44
                              )
                            : focusElement.values &&
                              focusElement.values.length > 0 &&
                              focusElement.values[0]}
                        </Grid>
                        <Grid item className="text-87 bold-500">
                          {typeCorrection
                            ? `${I18n.get("site_valueCorrected")} / ${
                                typeCorrection === LPR ? OCR : LPR
                              } to ${
                                typeCorrection === LPR ? LPR : OCR
                              } ${I18n.get("site_by")}`
                            : I18n.get("site_valueCorrectedBy")}
                        </Grid>
                      </>
                    ) : (
                      <Grid item className="text-87 bold-500">
                        {typeCorrection
                          ? `${I18n.get("site_valueConfirmed")} / ${
                              typeCorrection === LPR ? OCR : LPR
                            } to ${
                              typeCorrection === LPR ? LPR : OCR
                            } ${I18n.get("site_by")}`
                          : I18n.get("site_valueConfirmedBy")}
                      </Grid>
                    )}
                    <Grid item className="text-54">
                      {`${e.created_by}, ${formatDate(e.created_on)}`}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container direction="column">
      <FocusPhoto focusElement={focusElement} />
      {step === steps.LOADING ? (
        <Grid item className="text-center" style={{ margin: "15px" }}>
          <CircularProgress size={15} />
        </Grid>
      ) : (
        <>
          {!forceEdition && renderValues()}
          {(forceEdition ||
            !elementStatus.events ||
            elementStatus.events.length === 0) &&
            (step === steps.INITIAL ? (
              <Grid item style={{ paddingTop: "15px" }}>
                <span className="text-54">
                  {I18n.get("site_isTheCorrect") + " "}
                </span>
                <span className="text-87 bold-500">{focusElement.type}</span>
                <span className="text-54">
                  {" " + I18n.get("site_value") + "?"}
                </span>
                <CustomButton
                  color="primary"
                  variant="contained"
                  style={{ margin: "0px 10px" }}
                  onClick={confirmValue}
                  disabled={
                    elementStatus.events && elementStatus.events.length > 0
                  }
                >
                  {I18n.get("site_yes")}
                </CustomButton>
                <CustomButton
                  color="primary"
                  variant="contained"
                  onClick={() => {handleIncorrectValueTransition()}}
                >
                  {I18n.get("site_no")}
                </CustomButton>
              </Grid>
            ) : step === steps.INCORRECT_VALUE ? (
              <>
                <Grid item>
                  <Grid container direction="column">
                    <Grid item style={{ margin: "7px 0px" }}>
                      <Grid container alignItems="center">
                        <Grid item xs={5} className="text-87 bold-500">
                          {I18n.get("site_valueType")}
                        </Grid>
                        <Grid item xs>
                          <CustomSelect
                            name="valueType"
                            value={valueType}
                            options={correctionTypeOptions}
                            onChange={(e) => setValueType(e.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item style={{ marginBottom: "7px" }}>
                      <Grid container alignItems="center">
                        <Grid xs={5} item className="text-87 bold-500">
                          {I18n.get("site_typeCorrectValue")}
                        </Grid>
                        <Grid item xs>
                          <CustomInput
                            name="correction"
                            value={inputValue}
                            onChange={handleChange}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item className="text-center">
                      <CustomButton
                        color="primary"
                        style={{ marginRight: "10px" }}
                        onClick={() => {
                          forceEdition
                            ? setForceEdition(false)
                            : setStep(steps.INITIAL);
                        }}
                      >
                        {I18n.get("site_cancel")}
                      </CustomButton>
                      <CustomButton
                        color="primary"
                        variant="contained"
                        style={{ marginLeft: "10px" }}
                        onClick={correctValue}
                        disabled={focusElement.text === inputValue}
                      >
                        {I18n.get("site_save")}
                      </CustomButton>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            ) : (
              <></>
            ))}
        </>
      )}
    </Grid>
  );
};

Correction.propTypes = {
  focusElement: PropTypes.object.isRequired,
  detectionEventGroup: PropTypes.object,
  reloadEventGroupAndFocusElement: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
  };
};

export default connect(mapStateToProps)(Correction);
